import React, {useEffect} from 'react'
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {DateInput} from '../../../common/component/DateInput'
import {SelectInput} from '../../../common/component/SelectInput'
import {TextInput} from '../../../common/component/TextInput'
import WizardSectionCard from './sections/WizardSectionCard'
import {RadioButtonInput} from '../../../common/component/RadioButtonInput'
import {MobileNumberInput} from '../../../common/component/MobileNumberInput'

interface ProfileStepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  trigger: UseFormTrigger<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const emergencyRelationshipOptions = [
  {label: 'Parent', value: 'parent'},
  {label: 'Sibling', value: 'sibling'},
  {label: 'Partner / Spouse', value: 'partner'},
  {label: 'Relative', value: 'relative'},
  {label: 'Friend', value: 'friend'},
  {label: 'Housemate', value: 'housemate'},
  {label: 'Guardian', value: 'guardian'},
  {label: 'Other', value: 'other'},
]

const australianStatusOptions = [
  {label: 'Australian citizen', value: 'Australian citizen'},
  {label: 'Permanent resident', value: 'Permanent resident'},
  {label: 'Temporary visa', value: 'Temporary visa'},
]

const DATE_OF_BIRTH_MIN_YEAR_OFFSET = 106
const DATE_OF_BIRTH_MAX_YEAR_OFFSET = 11

const getDateOfBirthRange = () => {
  const currentYear = new Date().getFullYear()

  return {
    minDate: new Date(currentYear - DATE_OF_BIRTH_MIN_YEAR_OFFSET, 0, 1),
    maxDate: new Date(currentYear - DATE_OF_BIRTH_MAX_YEAR_OFFSET, 11, 31),
  }
}

const formatMobile = (value?: string): string => {
  const digits = value?.replace(/\D/g, '') ?? ''
  if (!/^04\d{8}$/.test(digits)) {
    return value ?? ''
  }

  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
}

const ProfileStep: React.FC<ProfileStepProps> = ({
  register,
  control,
  errors,
  watch,
  setValue,
  trigger,
  triggerValidation,
}) => {
  const mobileNumber = watch('mobileNumber')
  const australianStatus = watch('australianStatus')
  const dateOfBirthRange = getDateOfBirthRange()

  useEffect(() => {
    if (triggerValidation) triggerValidation()
  }, [])

  const handleAustralianStatusChoice = (value: string) => {
    const ausStatus = value
    if (ausStatus === undefined) {
      return
    }

    setValue('australianStatus', ausStatus, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
    setValue('isVisaHolder', ausStatus === 'Temporary visa', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  return (
    <div>
      <div className='wizard-hint-card mb-4 d-flex' style={{padding: '20px'}}>
        <i className='bi bi-info-circle me-5 gradient-icon'></i>
        <span>
          <strong>This is your rental hello.</strong> Agents see this section first when they open
          your card.
        </span>
      </div>
      <div className='d-grid gap-4'>
        <WizardSectionCard title='' className='p-8'>
          <TextInput
            name='email'
            label='Email Address'
            placeholder='john@example.com'
            register={register}
            errors={errors}
            required
          />
          <hr className='mt-5 mb-4 text-muted'></hr>
          <div className='mb-5'>
            <h5 className='subheader'>PERSONAL DETAILS</h5>
            <span className='secondary text-muted'></span>
          </div>
          <DateInput
            name='dateOfBirth'
            label='Date of Birth'
            control={control}
            errors={errors}
            required='Date of birth is required.'
            minDate={dateOfBirthRange.minDate}
            maxDate={dateOfBirthRange.maxDate}
          />
          <RadioButtonInput<IApplicationFormData>
            name='australianStatus'
            label="What's your status in Australia?"
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={australianStatusOptions}
            value={australianStatus}
            defaultValue={australianStatus}
            onChange={handleAustralianStatusChoice}
          />
          <hr className='mt-5 mb-4 text-muted'></hr>
          <div className='mb-5' data-wizard-scroll-target='emergency-contact'>
            <h5 className='subheader'>EMERGENCY CONTACT</h5>
            <span className='secondary text-muted'></span>
          </div>

          <TextInput
            name='emergencyContactName'
            label='Name'
            placeholder='Their full name'
            register={register}
            errors={errors}
            required
            className='mt-6'
          />

          <MobileNumberInput
            name='emergencyContactPhone'
            label='Phone'
            placeholder='Mobile (0412 345 678)'
            control={control}
            errors={errors}
            required
            className='wizard-start-field'
            inputClassName='wizard-start-input'
          />

          <SelectInput
            name='emergencyContactRelationship'
            label='Relationship'
            register={register}
            errors={errors}
            required
            optionsList={emergencyRelationshipOptions}
          />
        </WizardSectionCard>
      </div>
    </div>
  )
}

export default ProfileStep
